import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Typography,
	Button,
	TextField,
	Tooltip,
	Paper,
	Chip,
	IconButton,
	useTheme,
	CircularProgress,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';
import { Add, Edit, Delete, PersonOutlined, Visibility } from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import FilterList from '@mui/icons-material/FilterList';
import MenuIcon from '@mui/icons-material/Menu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import {
	SystemNotificationReceiverDialog,
	SystemNotificationReceiverRecord,
} from './SystemNotificationReceiverDialog';
import { SystemNotificationReceiverViewDialog } from './SystemNotificationReceiverViewDialog';

const VIEWMODE_STORAGE_KEY = 'system-notification-receivers-view-mode';
const TILE_PAGE_SIZE = 12;

const tableSchema = {
	paths: {
		_id: { instance: 'String', isRequired: false },
		title: { instance: 'String', isRequired: true },
		firstName: { instance: 'String', isRequired: true },
		lastName: { instance: 'String', isRequired: true },
		whatsappNumber: { instance: 'String', isRequired: true },
		mobileNumber: { instance: 'String', isRequired: true },
		email: { instance: 'String', isRequired: false },
		updatedAt: { instance: 'Date', isRequired: false },
	},
	obj: {},
};

export const SystemNotificationReceivers: React.FC = () => {
	const theme = useTheme();

	const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
		const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
		return stored === 'table' ? 'table' : 'tile';
	});
	const [searchQuery, setSearchQuery] = useState('');
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);

	const [receivers, setReceivers] = useState<SystemNotificationReceiverRecord[]>([]);
	const [loading, setLoading] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingReceiver, setEditingReceiver] = useState<SystemNotificationReceiverRecord | null>(null);
	const [viewDialogOpen, setViewDialogOpen] = useState(false);
	const [viewingReceiver, setViewingReceiver] = useState<SystemNotificationReceiverRecord | null>(null);

	const heroTileFg = theme.palette.dashboard?.heroTileForegroundColor;
	const heroTileLabelColor = theme.palette.dashboard?.heroTileLabelColor
		?? (heroTileFg ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255,255,255,0.7)');
	const heroTileTextShadow = heroTileFg ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
	const tileGradient = theme.palette.dashboard?.counterHeroGradient
		?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;

	const fetchReceivers = useCallback(async () => {
		setLoading(true);
		try {
			const response = await apiClient.get('/system-notifications/receivers/all');
			setReceivers(Array.isArray(response.data) ? response.data : []);
		} catch (error) {
			Logger.error('SystemNotificationReceivers', `Failed to fetch receivers: ${error}`);
			setReceivers([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchReceivers();
	}, [fetchReceivers]);

	const filteredReceivers = useMemo(() => {
		if (!searchQuery.trim()) return receivers;
		const q = searchQuery.toLowerCase();
		return receivers.filter(
			(r) =>
				r.firstName.toLowerCase().includes(q)
				|| r.lastName.toLowerCase().includes(q)
				|| r.title.toLowerCase().includes(q)
				|| r.whatsappNumber.toLowerCase().includes(q)
				|| r.mobileNumber.toLowerCase().includes(q)
				|| (r.email || '').toLowerCase().includes(q),
		);
	}, [receivers, searchQuery]);

	useEffect(() => {
		setTileVisibleCount(TILE_PAGE_SIZE);
	}, [searchQuery, receivers]);

	const paginatedReceivers = useMemo(
		() => filteredReceivers.slice(0, tileVisibleCount),
		[filteredReceivers, tileVisibleCount],
	);
	const hasMoreTiles = tileVisibleCount < filteredReceivers.length;

	const displayName = (r: SystemNotificationReceiverRecord) =>
		`${r.title} ${r.firstName} ${r.lastName}`.trim();

	const formatUpdatedAt = (value?: string) => {
		if (!value) return '—';
		try {
			return new Date(value).toLocaleDateString();
		} catch {
			return value;
		}
	};

	const handleNewReceiver = () => {
		setEditingReceiver(null);
		setDialogOpen(true);
	};

	const handleView = (receiver: SystemNotificationReceiverRecord) => {
		setViewingReceiver(receiver);
		setViewDialogOpen(true);
	};

	const handleEdit = (receiver: SystemNotificationReceiverRecord) => {
		setEditingReceiver(receiver);
		setDialogOpen(true);
	};

	const handleDelete = async (receiver: SystemNotificationReceiverRecord) => {
		if (!window.confirm(`Delete receiver "${displayName(receiver)}"?`)) return;
		try {
			await apiClient.delete(`/system-notifications/receivers/${receiver._id}`);
			setRefreshKey((k) => k + 1);
			fetchReceivers();
		} catch (error) {
			Logger.error('SystemNotificationReceivers', `Failed to delete receiver: ${error}`);
		}
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
		setEditingReceiver(null);
	};

	const handleDialogSave = () => {
		setDialogOpen(false);
		setEditingReceiver(null);
		setRefreshKey((k) => k + 1);
		fetchReceivers();
	};

	const customActions = useCallback((record: any) => {
		return [{
			label: 'View Details',
			onClick: () => handleView(record),
			color: 'info' as const,
			icon: <Visibility />,
		}];
	}, []);

	const toolbarBgSx = {
		backgroundColor: (t: any) =>
			t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
	};

	const viewToggle = (
		<ToggleButtonGroup
			value={viewMode}
			exclusive
			onChange={(_e, newMode) => {
				if (newMode !== null) {
					setViewMode(newMode);
					localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
				}
			}}
			size="small"
			sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
		>
			<ToggleButton value="tile" aria-label="Tile view">
				<Tooltip title="Tile View">
					<ViewModuleIcon fontSize="small" />
				</Tooltip>
			</ToggleButton>
			<ToggleButton value="table" aria-label="Table view">
				<Tooltip title="Table View">
					<TableRowsIcon fontSize="small" />
				</Tooltip>
			</ToggleButton>
		</ToggleButtonGroup>
	);

	const renderReceiverTile = (receiver: SystemNotificationReceiverRecord) => (
		<Box
			key={receiver._id}
			onClick={() => handleView(receiver)}
			sx={{
				width: 260,
				height: 200,
				borderRadius: 2.5,
				overflow: 'hidden',
				position: 'relative',
				boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
				flexShrink: 0,
				border: '1px solid',
				borderColor: 'divider',
				cursor: 'pointer',
				'&:hover .tile-overlay': { height: 120 },
				'&:hover .tile-details': { opacity: 1 },
				'&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
				'@media (hover: none)': {
					'& .tile-overlay': { height: 120 },
					'& .tile-details': { opacity: 1 },
					'& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
				},
			}}
		>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: tileGradient,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 0.5,
					color: heroTileFg ?? '#fff',
				}}
			>
				<PersonOutlined sx={{ fontSize: 40, opacity: 0.85, color: heroTileFg ?? '#fff' }} />
				<Typography
					sx={{
						fontSize: '0.95rem',
						fontWeight: 700,
						color: heroTileFg ?? '#fff',
						lineHeight: 1.2,
						textShadow: heroTileTextShadow,
						textAlign: 'center',
						px: 1.5,
						wordBreak: 'break-word',
					}}
				>
					{receiver.title} {receiver.firstName}
				</Typography>
				<Typography
					sx={{
						fontSize: '0.7rem',
						fontWeight: 600,
						color: heroTileLabelColor,
						textTransform: 'uppercase',
						letterSpacing: 1,
					}}
				>
					Receiver
				</Typography>
			</Box>

			<Box
				className="tile-overlay"
				sx={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					right: 0,
					overflow: 'hidden',
					background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
					transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
					height: 40,
					px: 1.5,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start',
				}}
			>
				<Typography
					variant="subtitle2"
					noWrap
					className="tile-name-short"
					sx={{
						color: '#fff',
						fontWeight: 700,
						fontSize: '0.82rem',
						lineHeight: '40px',
						textShadow: '0 1px 3px rgba(0,0,0,0.4)',
						flexShrink: 0,
						transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
					}}
				>
					{receiver.mobileNumber}
				</Typography>

				<Box
					className="tile-details"
					sx={{
						opacity: 0,
						transition: 'opacity 0.3s ease 0.08s',
						display: 'flex',
						flexDirection: 'column',
						gap: 0.5,
						pt: 0.25,
						pb: 1,
						flex: 1,
					}}
				>
					<Typography
						variant="caption"
						sx={{
							color: 'rgba(255,255,255,0.7)',
							fontSize: '0.68rem',
							lineHeight: 1.2,
							wordBreak: 'break-all',
						}}
					>
						{receiver.email || 'No email'}
					</Typography>
					<Typography
						variant="caption"
						sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}
					>
						Updated: {formatUpdatedAt(receiver.updatedAt)}
					</Typography>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
						<Box display="flex" gap={0.5}>
							<Tooltip title="Edit">
								<Button
									color="secondary"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleEdit(receiver);
									}}
									sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
								>
									<Edit fontSize="small" />
								</Button>
							</Tooltip>
						</Box>
						<Box display="flex" gap={0.5}>
							<Tooltip title="Delete">
								<Button
									color="error"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleDelete(receiver);
									}}
									sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
								>
									<Delete fontSize="small" />
								</Button>
							</Tooltip>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);

	const renderTileView = () => (
		<Box display="flex" flexDirection="column" gap={2.5}>
			<Box
				display="flex"
				flexWrap="wrap"
				gap={2.5}
				sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}
			>
				{paginatedReceivers.map((receiver) => renderReceiverTile(receiver))}
			</Box>

			{filteredReceivers.length === 0 && !loading && (
				<Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
					<Typography color="text.secondary">
						{searchQuery
							? 'No receivers match your search.'
							: 'No receivers found. Create one to get started.'}
					</Typography>
				</Paper>
			)}

			{filteredReceivers.length > 0 && (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						Showing {paginatedReceivers.length} of {filteredReceivers.length} receiver
						{filteredReceivers.length !== 1 ? 's' : ''}
					</Typography>
					{hasMoreTiles && (
						<Button
							variant="outlined"
							color="secondary"
							onClick={() => setTileVisibleCount((prev) => prev + TILE_PAGE_SIZE)}
						>
							Load More
						</Button>
					)}
				</Box>
			)}
		</Box>
	);

	const renderTableView = () => (
		<DataTableWithStaticSchema
			dataUrl="/system-notifications/receivers/all"
			schemaObject={tableSchema}
			columnsToExclude={['_id']}
			refreshTrigger={refreshKey}
			externalSearchQuery={searchQuery}
			hideSearch
			onEdit={(record: any) => handleEdit(record)}
			onDelete={(record: any) => handleDelete(record)}
			customActions={customActions}
			columnRenderers={{
				title: (row: any) => (
					<Chip
						label={row.title}
						size="small"
						color="secondary"
						sx={{ fontWeight: 700, height: 22, fontSize: '0.68rem' }}
					/>
				),
			}}
		/>
	);

	return (
		<Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
			<DynamicBreadcrumbs
				items={[
					{ itemPath: '/dashboard', itemLabel: 'Administration' },
					{ itemPath: '/administration/system-notifications', itemLabel: 'System Notifications' },
					{ itemPath: '/administration/system-notifications/receivers', itemLabel: 'Receivers' },
				]}
			/>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					gap: { xs: 1, md: 2 },
					alignItems: 'center',
					mb: 3,
					p: 1.5,
					borderRadius: 2,
					...toolbarBgSx,
				}}
			>
				<Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
					<IconButton
						color="secondary"
						onClick={() => setMobileMenuOpen(true)}
						size="large"
						sx={{
							border: '1px solid',
							borderColor: 'secondary.main',
							'&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
						}}
					>
						<FilterList />
					</IconButton>
				</Box>
				<Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
					<IconButton
						color="secondary"
						onClick={() => setToolbarMenuOpen(true)}
						size="large"
						sx={{
							border: '1px solid',
							borderColor: 'secondary.main',
							'&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
						}}
					>
						<MenuIcon />
					</IconButton>
				</Box>

				<Box
					sx={{
						display: { xs: 'none', md: 'flex' },
						flexGrow: 1,
						flexWrap: 'nowrap',
						alignItems: 'center',
						gap: 1,
					}}
				>
					<TextField
						placeholder="Search Receivers"
						label="Search Receivers"
						variant="outlined"
						size="small"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						sx={{ flexGrow: 1, minWidth: '220px', flexShrink: 1 }}
					/>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							flexWrap: 'nowrap',
							flexShrink: 0,
							ml: 'auto',
						}}
					>
						{viewToggle}
						<Button
							variant="outlined"
							color="secondary"
							startIcon={<Add />}
							onClick={handleNewReceiver}
							size="small"
							sx={{ whiteSpace: 'nowrap' }}
						>
							New Receiver
						</Button>
					</Box>
				</Box>
			</Box>

			<Dialog open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>Search &amp; Filters</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} pt={1}>
						<TextField
							placeholder="Search Receivers"
							label="Search Receivers"
							variant="outlined"
							size="small"
							fullWidth
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setMobileMenuOpen(false)}>Done</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>Actions</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} pt={1} alignItems="stretch">
						<Box display="flex" justifyContent="center">
							{viewToggle}
						</Box>
						<Button
							variant="outlined"
							color="secondary"
							startIcon={<Add />}
							onClick={() => {
								setToolbarMenuOpen(false);
								handleNewReceiver();
							}}
							fullWidth
						>
							New Receiver
						</Button>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setToolbarMenuOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>

			{loading && receivers.length === 0 ? (
				<Box display="flex" justifyContent="center" p={4}>
					<CircularProgress color="secondary" />
				</Box>
			) : viewMode === 'tile' ? (
				renderTileView()
			) : (
				renderTableView()
			)}

			{dialogOpen && (
				<SystemNotificationReceiverDialog
					open={dialogOpen}
					onClose={handleDialogClose}
					onSave={handleDialogSave}
					receiver={editingReceiver}
				/>
			)}

			<SystemNotificationReceiverViewDialog
				open={viewDialogOpen}
				onClose={() => {
					setViewDialogOpen(false);
					setViewingReceiver(null);
				}}
				receiver={viewingReceiver}
			/>
		</Box>
	);
};

export default SystemNotificationReceivers;
